// Pre-game host panel: QR, room code, share link, start game

import React, { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { createLogger } from '@airhead-games/shared/utilities';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { getShareableFrontendUrl } from '@/utilities/getShareableFrontendUrl';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const logger = createLogger('RoomLobbyPanel');

export interface RoomLobbyPanelProps {
  roomCode: string;
  qrCode?: string;
  onStartGame: () => Promise<void> | void;
  buttonLabel: React.ReactNode;
  disabled: boolean;
  starting: boolean;
  error?: string | null;
  onStartButtonHoverChange?: (hovered: boolean) => void;
}

const RoomLobbyPanel: React.FC<RoomLobbyPanelProps> = ({
  roomCode,
  qrCode,
  onStartGame,
  buttonLabel,
  disabled,
  starting,
  error,
  onStartButtonHoverChange,
}) => {
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const copyTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (copyTooltipTimeoutRef.current) {
        clearTimeout(copyTooltipTimeoutRef.current);
      }
    };
  }, []);

  const copyRoomLink = () => {
    const baseUrl = getShareableFrontendUrl();
    const url = `${baseUrl}/join?${roomCode}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopyTooltipOpen(true);
        if (copyTooltipTimeoutRef.current) {
          clearTimeout(copyTooltipTimeoutRef.current);
        }
        copyTooltipTimeoutRef.current = setTimeout(() => {
          setCopyTooltipOpen(false);
        }, 1000);
      })
      .catch((err) => {
        logger.error('Failed to copy link:', err);
      });
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex w-full max-w-xs flex-col items-center justify-center gap-6">
        {qrCode ? (
          <div className="text-center">
            <img
              src={qrCode}
              alt="QR Code"
              className="h-auto w-60 max-w-full"
            />
          </div>
        ) : null}

        <div className="text-center">
          <span className="text-6xl font-bold text-primary">{roomCode}</span>
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <Tooltip
            open={copyTooltipOpen}
            onOpenChange={(open) => {
              if (!open) {
                setCopyTooltipOpen(false);
              }
            }}
          >
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="xl"
                className="w-full max-w-[240px] cursor-pointer"
                onClick={copyRoomLink}
              >
                <Copy data-icon="inline-start" className="size-4" />
                Share link
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copied!</TooltipContent>
          </Tooltip>

          <div
            className="w-full max-w-[240px]"
            onMouseEnter={() => onStartButtonHoverChange?.(true)}
            onMouseLeave={() => onStartButtonHoverChange?.(false)}
            onFocusCapture={() => onStartButtonHoverChange?.(true)}
            onBlurCapture={() => onStartButtonHoverChange?.(false)}
          >
            <Button
              type="button"
              size="xl"
              className="w-full max-w-[240px] cursor-pointer"
              disabled={disabled}
              onClick={() => void onStartGame()}
            >
              {starting ? (
                <>
                  <Spinner data-icon="inline-start" className="size-4" />
                  Starting game
                </>
              ) : (
                buttonLabel
              )}
            </Button>
          </div>

          {error ? (
            <p className="m-0 text-center text-sm text-destructive">{error}</p>
          ) : null}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default RoomLobbyPanel;
